import { strToU8, unzipSync, zipSync } from "fflate";
import type { PaletteEntry, PatternProject } from "./types";
import { completedCount, countByPalette, occupiedCount } from "./pattern";

function cloneBuffer(view: Uint8Array | Uint16Array): Uint8Array {
  return new Uint8Array(view.buffer.slice(view.byteOffset, view.byteOffset + view.byteLength));
}

async function sha256(data: Uint8Array): Promise<string> {
  const input = data.buffer.slice(data.byteOffset, data.byteOffset + data.byteLength) as ArrayBuffer;
  const digest = await crypto.subtle.digest("SHA-256", input);
  return [...new Uint8Array(digest)].map((byte) => byte.toString(16).padStart(2, "0")).join("");
}

export async function exportProjectBundle(project: PatternProject, source?: Blob, preview?: Blob): Promise<Blob> {
  const cells = cloneBuffer(project.cells);
  const completed = cloneBuffer(project.completed);
  const confidence = cloneBuffer(project.confidence);
  const sourceBytes = source ? new Uint8Array(await source.arrayBuffer()) : new Uint8Array();
  const previewBytes = preview ? new Uint8Array(await preview.arrayBuffer()) : new Uint8Array();
  const manifest = {
    schemaVersion: 1,
    project: { ...project, cells: undefined, completed: undefined, confidence: undefined },
    total: occupiedCount(project),
    files: {
      "cells.u16": await sha256(cells),
      "completed.bin": await sha256(completed),
      "confidence.bin": await sha256(confidence),
      "source.webp": await sha256(sourceBytes),
      "preview.png": await sha256(previewBytes)
    }
  };
  const zip = zipSync({
    "manifest.json": strToU8(JSON.stringify(manifest, null, 2)),
    "cells.u16": cells,
    "completed.bin": completed,
    "confidence.bin": confidence,
    "source.webp": sourceBytes,
    "preview.png": previewBytes
  }, { level: 6 });
  return new Blob([zip], { type: "application/vnd.pindou+zip" });
}

export async function importProjectBundle(blob: Blob): Promise<{ project: PatternProject; source?: Blob; preview?: Blob }> {
  const files = unzipSync(new Uint8Array(await blob.arrayBuffer()));
  const manifestBytes = files["manifest.json"];
  if (!manifestBytes) throw new Error("备份包缺少 manifest.json");
  const manifest = JSON.parse(new TextDecoder().decode(manifestBytes));
  if (manifest.schemaVersion !== 1) throw new Error("不支持的项目版本");
  for (const [name, expected] of Object.entries(manifest.files as Record<string, string>)) {
    const bytes = files[name];
    if (!bytes || await sha256(bytes) !== expected) throw new Error(`文件校验失败：${name}`);
  }
  const meta = manifest.project as Omit<PatternProject, "cells" | "completed" | "confidence">;
  const cellsBytes = files["cells.u16"]!;
  const cellsBuffer = cellsBytes.buffer.slice(cellsBytes.byteOffset, cellsBytes.byteOffset + cellsBytes.byteLength) as ArrayBuffer;
  const completedBytes = files["completed.bin"]!;
  const confidenceBytes = files["confidence.bin"]!;
  const project: PatternProject = {
    ...meta,
    id: crypto.randomUUID(),
    name: `${meta.name}（恢复）`,
    cells: new Uint16Array(cellsBuffer),
    completed: completedBytes.slice(),
    confidence: confidenceBytes.slice(),
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString()
  };
  if (project.cells.length !== project.width * project.height || project.completed.length !== project.cells.length) {
    throw new Error("备份包尺寸与单元格数据不一致");
  }
  if (project.confidence.length !== project.cells.length) throw new Error("备份包置信度数据长度不一致");
  const sourceBytes = files["source.webp"]!;
  const previewBytes = files["preview.png"]!;
  return {
    project,
    source: sourceBytes.length ? new Blob([sourceBytes], { type: "image/webp" }) : undefined,
    preview: previewBytes.length ? new Blob([previewBytes], { type: "image/png" }) : undefined
  };
}

function rgb(entry: PaletteEntry): string { return `rgb(${entry.displayRGB.join(",")})`; }

export function projectToSvg(project: PatternProject, includeCodes = true): Blob {
  const cell = 20;
  const header = 24;
  const width = project.width * cell + header * 2;
  const height = project.height * cell + header * 2;
  const palette = new Map(project.palette.map((entry) => [entry.id, entry]));
  const rects: string[] = [];
  const labels: string[] = [];
  for (let row = 0; row < project.height; row += 1) {
    for (let column = 0; column < project.width; column += 1) {
      const id = project.cells[row * project.width + column] ?? 0;
      if (!id) continue;
      const entry = palette.get(id);
      if (!entry) continue;
      const x = header + column * cell;
      const y = header + row * cell;
      rects.push(`<rect x="${x}" y="${y}" width="${cell}" height="${cell}" fill="${rgb(entry)}"/>`);
      if (includeCodes) labels.push(`<text x="${x + cell / 2}" y="${y + 13}" text-anchor="middle" font-size="7" fill="#111827">${entry.code}</text>`);
    }
  }
  const lines: string[] = [];
  for (let column = 0; column <= project.width; column += 1) {
    const x = header + column * cell;
    lines.push(`<line x1="${x}" y1="${header}" x2="${x}" y2="${height - header}" stroke="${column % 5 === 0 ? "#64748b" : "#d8dee8"}" stroke-width="${column % 5 === 0 ? 1.2 : .5}"/>`);
  }
  for (let row = 0; row <= project.height; row += 1) {
    const y = header + row * cell;
    lines.push(`<line x1="${header}" y1="${y}" x2="${width - header}" y2="${y}" stroke="${row % 5 === 0 ? "#64748b" : "#d8dee8"}" stroke-width="${row % 5 === 0 ? 1.2 : .5}"/>`);
  }
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="${width}" height="${height}" viewBox="0 0 ${width} ${height}"><rect width="100%" height="100%" fill="#fff"/>${rects.join("")}${lines.join("")}${labels.join("")}</svg>`;
  return new Blob([svg], { type: "image/svg+xml" });
}

export function projectToCsv(project: PatternProject): Blob {
  const counts = countByPalette(project);
  const rows = ["色号,总数,已完成,剩余"];
  for (const entry of project.palette) {
    const count = counts.get(entry.id) ?? { total: 0, completed: 0 };
    rows.push(`${entry.code},${count.total},${count.completed},${count.total - count.completed}`);
  }
  return new Blob(["\uFEFF", rows.join("\r\n")], { type: "text/csv;charset=utf-8" });
}

export async function projectToPng(project: PatternProject, options: { includeCodes?: boolean; includeCoordinates?: boolean } = {}): Promise<Blob> {
  const cell = 28;
  const margin = options.includeCoordinates === false ? 0 : 28;
  const canvas = document.createElement("canvas");
  canvas.width = project.width * cell + margin * 2;
  canvas.height = project.height * cell + margin * 2;
  const context = canvas.getContext("2d", { alpha: false });
  if (!context) throw new Error("无法创建 PNG 导出画布");
  context.fillStyle = "#fff";
  context.fillRect(0, 0, canvas.width, canvas.height);
  const palette = new Map(project.palette.map((entry) => [entry.id, entry]));
  context.textAlign = "center";
  context.textBaseline = "middle";
  context.font = "9px -apple-system, BlinkMacSystemFont, sans-serif";
  for (let row = 0; row < project.height; row += 1) {
    for (let column = 0; column < project.width; column += 1) {
      const entry = palette.get(project.cells[row * project.width + column] ?? 0);
      if (!entry) continue;
      const x = margin + column * cell;
      const y = margin + row * cell;
      context.fillStyle = rgb(entry);
      context.fillRect(x, y, cell, cell);
      if (options.includeCodes !== false) {
        const lightness = entry.displayRGB[0] * .299 + entry.displayRGB[1] * .587 + entry.displayRGB[2] * .114;
        context.fillStyle = lightness < 120 ? "#fff" : "#111827";
        context.fillText(entry.code, x + cell / 2, y + cell / 2);
      }
    }
  }
  for (let column = 0; column <= project.width; column += 1) {
    context.beginPath();
    context.strokeStyle = column % 5 === 0 ? "#64748b" : "#d8dee8";
    context.lineWidth = column % 5 === 0 ? 2 : 1;
    const x = margin + column * cell;
    context.moveTo(x, margin); context.lineTo(x, canvas.height - margin); context.stroke();
  }
  for (let row = 0; row <= project.height; row += 1) {
    context.beginPath();
    context.strokeStyle = row % 5 === 0 ? "#64748b" : "#d8dee8";
    context.lineWidth = row % 5 === 0 ? 2 : 1;
    const y = margin + row * cell;
    context.moveTo(margin, y); context.lineTo(canvas.width - margin, y); context.stroke();
  }
  if (margin) {
    context.fillStyle = "#475467";
    context.font = "10px -apple-system, BlinkMacSystemFont, sans-serif";
    for (let column = 0; column < project.width; column += 1) {
      context.fillText(String(column + 1), margin + column * cell + cell / 2, margin / 2);
      context.fillText(String(column + 1), margin + column * cell + cell / 2, canvas.height - margin / 2);
    }
    for (let row = 0; row < project.height; row += 1) {
      context.fillText(String(row + 1), margin / 2, margin + row * cell + cell / 2);
      context.fillText(String(row + 1), canvas.width - margin / 2, margin + row * cell + cell / 2);
    }
  }
  return new Promise((resolve, reject) => canvas.toBlob((blob) => blob ? resolve(blob) : reject(new Error("PNG 导出失败")), "image/png"));
}

export function projectSummaryText(project: PatternProject): string {
  return `${project.width} × ${project.height} · ${project.palette.length} 色 · ${completedCount(project)} / ${occupiedCount(project)}`;
}

export function downloadBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = filename;
  anchor.click();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
